import React from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../../hooks";
import {
  fetchLifeInsuranceCoverageNeedListAsync,
  downloadLifeInsuranceCoverageNeedPDFAsync,
  previewLifeInsuranceCoverageNeedPDFAsync,
} from "../../../../../../../redux/lifeInsurancePolicy/lifeInsuranceCoverageNeed/lifeInsuranceCoverageNeedActions";
import { LoadState, MODULE_IDS } from "../../../../../../../constants/enums";
import { ILifeInsuranceCoverageNeed } from "../../../../../../../redux/lifeInsurancePolicy/lifeInsuranceCoverageNeed/lifeInsuranceCoverageNeed.types";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { useTableV2State } from "../../../../../../../components/TableV2/hooks/useTableV2State";
import { DataTableV2 } from "../../../../../../../components/TableV2/DataTableV2";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import { StatusRenderer } from "../../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { ControlledCustomSelect } from "../../../../../../../components/formsComponents/CustomSelect";
import { COMMON_STATUS_WITH_ALL } from "../../../../../../../constants/constants";
import { PageTitleBar } from "../../../../../../../components/PageTitleBar/PageTitleBar";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { AddOutlined } from "@mui/icons-material";

export const LifeInsuranceCoverageNeedList: React.FC = () => {
  const data1 = useOutletContext() as any;
  const customerId = data1[0];
  const customerPolicyId = data1[1];

  const navigate = useNavigate();

  const { list, listLoading, listError } = useSelector(
    (storeState: IStoreState) => storeState.lifeInsurance.coverageNeed
  );

  const dispatch = useDispatchWrapper();

  const [downloadLoading, setDownloadLoading] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "ACTIVE"
  );

  const {
    state: { columnsConfig },
    setColumnVisibility,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
  });

  const fetchList = () => {
    if (customerPolicyId) {
      dispatch(
        fetchLifeInsuranceCoverageNeedListAsync(customerPolicyId, status)
      );
    }
  };

  React.useEffect(() => {
    fetchList();
  }, [customerPolicyId, status]);

  const handleEditClick = (row: ILifeInsuranceCoverageNeed) => {
    navigate(
      `/life-insurance-policy/${customerId}/coverage-needs-tab/${customerPolicyId}/${row.incremental_number}`
    );
  };

  const handleViewClick = (row: ILifeInsuranceCoverageNeed) => {
    navigate(
      `/life-insurance-policy/${customerId}/coverage-needs-tab/${customerPolicyId}/${row.incremental_number}?viewMode=true`
    );
  };

  const handleDownloadClick = (row: ILifeInsuranceCoverageNeed) => {
    if (row.incremental_number !== undefined) {
      setDownloadLoading(true);
      dispatch(
        downloadLifeInsuranceCoverageNeedPDFAsync(
          customerPolicyId,
          row.incremental_number,
          () => {
            setDownloadLoading(false);
          }
        )
      );
    }
  };

  const handlePreviewClick = (row: ILifeInsuranceCoverageNeed) => {
    if (row.incremental_number !== undefined) {
      dispatch(
        previewLifeInsuranceCoverageNeedPDFAsync(
          customerPolicyId,
          row.incremental_number,
          () => {}
        )
      );
    }
  };

  const coverageNeedTableProps: IDataTableV2Props = {
    isPagination: false,
    isDataLoading: listLoading === LoadState.InProgress,
    uniqueRowKeyName: "incremental_number",
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {},
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) =>
                  setStatus(e.target.value as "ACTIVE" | "INACTIVE" | "-1")
                }
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS_WITH_ALL}
              />
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "actions",
        headerName: "Actions",
        fieldName: "",
        isFirstColumnSticky: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ILifeInsuranceCoverageNeed) => {
          return (
            <StandardTableActions
              onEditClick={() => handleEditClick(row)}
             // onViewClick={() => handleViewClick(row)}
            //  onDownLoadClick={() => handleDownloadClick(row)}
              //onDownloadPreview={() => handlePreviewClick(row)}
              //downloadLoading={downloadLoading}
            />
          );
        },
      },
      {
        key: "incremental_number",
        headerName: "#",
        fieldName: "incremental_number",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ILifeInsuranceCoverageNeed) => {
          return (
            <Link
              to={`/life-insurance-policy/${customerId}/coverage-needs-tab/${customerPolicyId}/${row.incremental_number}`}
            >
              {value.column.rowIndex+1}
            </Link>
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ILifeInsuranceCoverageNeed) => {
          return <StatusRenderer status={row.status || ""} />;
        },
      },
    ],
    rows: list,
  };

  return (
    <>
      <PageTitleBar
        heading="Coverage Needs"
        rightHeading={
          <RoleBasedCustomButton
            moduleKey={MODULE_IDS.LIFE_AND_FINCANCIAL}
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={() => {
              navigate(
                `/life-insurance-policy/${customerId}/coverage-needs-tab/${customerPolicyId}/create`
              );
            }}
          >
            Create
          </RoleBasedCustomButton>
        }
      />
      <PageLoader
        loading={listLoading === LoadState.InProgress}
        error={
          listError
            ? { message: listError }
            : null
        }
      >
        <DataTableV2 {...coverageNeedTableProps} />
      </PageLoader>
    </>
  );
};
